import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
/**
 * Fuel primitives (docs/rebuild/fuel.md sections 2 and 12). Money crosses the
 * wire as a decimal string with at most five fraction digits, like the
 * Decimal(18,5) columns; category fractions have at most five as well
 * (owner decision D14, 4 October 2026). A sixth digit is refused, never
 * rounded.
 */
export const FUEL_MONEY_SCALE = 5;
export const productSchema = z.enum(['DIESEL', 'PETROL']);
export const containerTypeSchema = z.enum(['BARREL', 'JERRYCAN']);
export const debtKindSchema = z.enum(['CHARGE', 'REPAYMENT']);
export const entryModeSchema = z.enum(['QUANTITIES', 'AMOUNT']);
export const customerLinkStatusSchema = z.enum(['LINKED', 'PENDING', 'INVALID_PHONE']);
/**
 * Why Customers finally refused the registration or phone change of a record
 * that is now «بانتظار الربط» (customers.md section 10.4): `PHONE_TAKEN`
 * («رفض نظام العملاء هذا الرقم، عدّل رقم الهاتف»), or a phone verification
 * receipt that expired or could not be applied («انتهت صلاحية تأكيد الرقم،
 * أعد التحقق», with the «إعادة التحقق» action).
 */
export const customerLinkErrorSchema = z.enum([
    'PHONE_TAKEN',
    'PHONE_VERIFICATION_RECEIPT_EXPIRED',
    'PHONE_VERIFICATION_INVALID',
    // Any other final refusal: shown with the same «رفض نظام العملاء هذا الرقم، عدل رقم الهاتف».
    'CUSTOMERS_REFUSED',
]);
/** A phone verification id from the verification dialog (customers.md section 10.4). */
export const phoneVerificationIdSchema = z.string().trim().min(1).max(64);
export const fuelAppSchema = z.enum(['dashboard', 'operator', 'pda']);
/** A signed decimal string with up to 13 integer and 5 fraction digits (Decimal(18,5)). */
export const fuelMoneySchema = z
    .string()
    .trim()
    .regex(/^-?\d{1,13}(\.\d{1,5})?$/, 'must be a decimal with at most 5 fraction digits');
/** A decimal string strictly greater than zero. */
export const positiveMoneySchema = fuelMoneySchema.refine((v) => !v.startsWith('-') && /[1-9]/.test(v), 'must be greater than zero');
/** A decimal string greater than or equal to zero. */
export const nonNegativeMoneySchema = fuelMoneySchema.refine((v) => !v.startsWith('-') || !/[1-9]/.test(v), 'must not be negative');
/** Category fraction (legacy name_as_number): > 0, up to 5 fraction digits (Decimal(9,5)). */
export const fractionSchema = z
    .string()
    .trim()
    .regex(/^\d{1,4}(\.\d{1,5})?$/, 'must be a decimal with at most 5 fraction digits')
    .refine((v) => /[1-9]/.test(v), 'must be greater than zero');
/** A business date, `YYYY-MM-DD`. */
export const businessDateSchema = z.iso.date();
/** One quantity per category, a whole number >= 0. */
export const quantityLineInputSchema = z.object({
    categoryId: z.string().min(1),
    qty: z.number().int().min(0).max(100_000),
});
/** One line per category: duplicates are refused (fixes the lost-mapping bug). */
export const quantityLinesSchema = z
    .array(quantityLineInputSchema)
    .max(100)
    .refine((lines) => new Set(lines.map((l) => l.categoryId)).size === lines.length, 'one line per category');
export const notesSchema = z.string().trim().max(1000).optional();
/**
 * Who did something, for list columns and details. `phone` is the employee's
 * phone from Unified Access (legacy column "رقم هاتف الموظف"); null for
 * migrated legacy accounts without one.
 */
export const actorSchema = z.object({
    id: z.string(),
    name: z.string(),
    phone: z.string().nullable(),
});
export const branchRefSchema = z.object({ id: z.string(), name: z.string() });
export const lineSchema = z.object({
    categoryId: z.string(),
    categoryName: z.string(),
    type: containerTypeSchema,
    fraction: z.string(),
    qty: z.number().int(),
});
/** Largest page a transaction list serves; the page-size picker offers up to 200. */
export const TRANSACTION_MAX_PAGE_SIZE = 200;
/** Filters shared by every transaction list (fuel.md section 5.2). */
export const transactionListQuerySchema = paginationQuerySchema.extend({
    /** Legacy lists show 50 rows per page. */
    pageSize: z.coerce.number().int().positive().max(TRANSACTION_MAX_PAGE_SIZE).default(50),
    product: productSchema,
    from: businessDateSchema.optional(),
    to: businessDateSchema.optional(),
    branchId: z.string().optional(),
});
/** Most selected rows one export takes (KAN-14), as many as one delete-many. */
export const EXPORT_SELECTED_MAX = 500;
/**
 * Export of the selected rows only (KAN-14). The ids travel in a POST body:
 * the selection survives paging, so it can outgrow a GET URL.
 */
export const exportSelectionSchema = z.object({
    ids: z.array(z.string().min(1)).min(1).max(EXPORT_SELECTED_MAX),
});
/** Several rows deleted in one confirmed action. */
export const deleteManySchema = z.object({ ids: z.array(z.string().min(1)).min(1).max(500) });
//# sourceMappingURL=primitives.js.map